import { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, ChevronUp, ChevronDown, X, Search, Dumbbell, ArrowLeft, Save, UserPlus, Pencil, Layers } from 'lucide-react';
import { useBackHandler } from '../../lib/backNav';
import { Button } from '../ui/button';

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']; // 0=Sun .. 6=Sat (US Sunday-first)
const BLOCK_TYPES = [
  { value: 'single', label: 'Straight sets' },
  { value: 'superset', label: 'Superset' },
  { value: 'circuit', label: 'Circuit' },
];
const ROLES = ['client', 'power_user', 'trainer', 'admin'];
const ROLE_LABELS = { client: 'Client', power_user: 'Power user', trainer: 'Trainer', admin: 'Admin' };
const COACH_TONES = [
  { v: 'encouraging', label: 'Encouraging' },
  { v: 'straightforward', label: 'Straightforward' },
  { v: 'tough', label: 'Tough' },
];

// ── Top-level: list of programs ↔ editor ────────────────────────────────────
const ProgramView = ({ api, token, user }) => {
  const [view, setView] = useState('list'); // 'list' | 'edit'
  const [editId, setEditId] = useState(null);
  const [tab, setTab] = useState('programs'); // 'programs' | 'clients'

  // Power users don't build/manage — they pick a saved program for themselves.
  if (user.role === 'power_user') return <MyProgramPicker api={api} token={token} user={user} />;

  if (view === 'edit') {
    return (
      <ProgramEditor
        api={api} token={token} programId={editId}
        onBack={() => { setView('list'); setEditId(null); }}
      />
    );
  }
  return (
    <div className="max-w-2xl mx-auto p-4">
      <div className="grid grid-cols-3 gap-1 mb-4 bg-muted rounded-lg p-1">
        {['programs', 'warmups', 'clients'].map((t) => (
          <button key={t} onClick={() => setTab(t)}
            className={`h-9 rounded-md text-sm font-medium capitalize ${tab === t ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground'}`}>
            {t}
          </button>
        ))}
      </div>
      {tab === 'programs'
        ? <ProgramList api={api} token={token} onEdit={(id) => { setEditId(id); setView('edit'); }} />
        : tab === 'warmups'
          ? <WarmupManager api={api} token={token} />
          : <ClientsView api={api} token={token} viewer={user} />}
    </div>
  );
};

// ── Program list ────────────────────────────────────────────────────────────
const ProgramList = ({ api, token, onEdit }) => {
  const [programs, setPrograms] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [assignFor, setAssignFor] = useState(null); // program being assigned

  const load = useCallback(() => {
    api.get('/programs', token).then(setPrograms).catch(() => setPrograms([]));
  }, [api, token]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    const name = newName.trim();
    if (!name) return;
    try {
      const { id } = await api.post('/programs', { name }, token);
      setCreating(false); setNewName('');
      onEdit(id);
    } catch { /* ignore */ }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete program "${p.name}"? This can't be undone.`)) return;
    try { await api.delete(`/programs/${p.id}`, token); load(); } catch { /* ignore */ }
  };

  return (
    <div>
      <div className="flex justify-end mb-3">
        {!creating && <Button onClick={() => setCreating(true)}><Plus className="h-4 w-4 mr-1" /> New program</Button>}
      </div>

      {creating && (
        <div className="flex gap-2 mb-4">
          <input
            autoFocus value={newName} onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && create()}
            placeholder="Program name"
            className="flex-1 h-10 rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
          <Button onClick={create}>Create</Button>
          <button onClick={() => { setCreating(false); setNewName(''); }} className="text-sm text-muted-foreground px-2">Cancel</button>
        </div>
      )}

      {programs === null ? (
        <p className="text-center text-muted-foreground py-12">Loading…</p>
      ) : programs.length === 0 ? (
        <div className="text-center text-muted-foreground py-12">
          <Layers className="h-10 w-10 mx-auto mb-3 opacity-40" />
          <p>No programs yet. Create one to start building.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {programs.map((p) => (
            <div key={p.id} className="rounded-lg border bg-card p-3">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-medium truncate">{p.name}</div>
                  <div className="text-xs text-muted-foreground">
                    {p.weeks} {p.weeks === 1 ? 'week' : 'weeks'} · {p.day_count} {p.day_count === 1 ? 'day' : 'days'} · {p.assigned_count} assigned · by {p.created_by_name}
                  </div>
                </div>
              </div>
              <div className="flex gap-2 mt-3">
                <Button variant="outline" className="h-9 flex-1" onClick={() => onEdit(p.id)}><Pencil className="h-4 w-4 mr-1" /> Edit</Button>
                <Button variant="outline" className="h-9 flex-1" onClick={() => setAssignFor(p)}><UserPlus className="h-4 w-4 mr-1" /> Assign</Button>
                <button onClick={() => remove(p)} className="h-9 w-9 rounded-md border flex items-center justify-center text-muted-foreground hover:text-destructive hover:border-destructive" aria-label="Delete">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {assignFor && <AssignModal api={api} token={token} program={assignFor} onClose={() => setAssignFor(null)} onChanged={load} />}
    </div>
  );
};

// ── Program editor (the builder) ────────────────────────────────────────────
const ProgramEditor = ({ api, token, programId, onBack }) => {
  useBackHandler(true, onBack);
  const [prog, setProg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [picker, setPicker] = useState(null); // { dayIdx, blockIdx } target for adding an exercise

  useEffect(() => {
    api.get(`/programs/${programId}`, token)
      .then((p) => setProg({
        ...p,
        days: (p.days || []).map((d) => ({
          ...d,
          blocks: (d.blocks || []).map((b) => ({ ...b, exercises: b.exercises || [] })),
        })),
      }))
      .catch(() => setProg(null));
  }, [api, token, programId]);

  // ── immutable tree helpers ──
  const patch = (updater) => setProg((p) => { const next = { ...p, days: p.days.map((d) => ({ ...d, blocks: d.blocks.map((b) => ({ ...b, exercises: [...b.exercises] })) })) }; updater(next); return next; });
  const setField = (k, v) => setProg((p) => ({ ...p, [k]: v }));

  const addDay = () => patch((p) => p.days.push({ week_no: 1, day_of_week: 1, focus: '', blocks: [] }));
  const removeDay = (di) => patch((p) => p.days.splice(di, 1));
  const updateDay = (di, k, v) => patch((p) => { p.days[di][k] = v; });

  const addBlock = (di) => patch((p) => p.days[di].blocks.push({ type: 'single', rounds: null, label: '', exercises: [] }));
  const removeBlock = (di, bi) => patch((p) => p.days[di].blocks.splice(bi, 1));
  const updateBlock = (di, bi, k, v) => patch((p) => { p.days[di].blocks[bi][k] = v; });
  const moveBlock = (di, bi, dir) => patch((p) => { const a = p.days[di].blocks; const j = bi + dir; if (j < 0 || j >= a.length) return; [a[bi], a[j]] = [a[j], a[bi]]; });

  const addExercise = (di, bi, lib) => patch((p) => p.days[di].blocks[bi].exercises.push({
    exercise_id: lib.id, name: lib.name, youtube_video_id: lib.youtube_video_id, muscle_group: lib.muscle_group,
    target_sets: 3, reps_text: '', suggested_weight: '', coach_note: '',
  }));
  const removeExercise = (di, bi, ei) => patch((p) => p.days[di].blocks[bi].exercises.splice(ei, 1));
  const updateExercise = (di, bi, ei, k, v) => patch((p) => { p.days[di].blocks[bi].exercises[ei][k] = v; });
  const moveExercise = (di, bi, ei, dir) => patch((p) => { const a = p.days[di].blocks[bi].exercises; const j = ei + dir; if (j < 0 || j >= a.length) return; [a[ei], a[j]] = [a[j], a[ei]]; });

  const save = async () => {
    setSaving(true); setSaved(false);
    // Derive reps_default (first int in reps_text) + suggested_text from weight.
    const days = prog.days.map((d) => ({
      week_no: d.week_no, day_of_week: d.day_of_week, focus: d.focus,
      blocks: d.blocks.map((b) => ({
        type: b.type, rounds: b.rounds, label: b.label,
        exercises: b.exercises.map((ex) => {
          const repsDefault = (String(ex.reps_text).match(/\d+/) || [])[0];
          const w = ex.suggested_weight === '' || ex.suggested_weight == null ? null : Number(ex.suggested_weight);
          return {
            exercise_id: ex.exercise_id, target_sets: ex.target_sets,
            reps_text: ex.reps_text, reps_default: repsDefault != null ? Number(repsDefault) : null,
            suggested_weight: w, suggested_text: w != null ? (w === 0 ? 'Bodyweight' : `${w} lb`) : null,
            coach_note: ex.coach_note,
          };
        }),
      })),
    }));
    try {
      await api.put(`/programs/${prog.id}`, { name: prog.name, description: prog.description, days }, token);
      setSaved(true); setTimeout(() => setSaved(false), 2000);
    } catch { /* ignore */ } finally { setSaving(false); }
  };

  if (!prog) return <p className="text-center text-muted-foreground py-16">Loading…</p>;

  return (
    <div className="max-w-2xl mx-auto p-4 pb-8">
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-3">
        <ArrowLeft className="h-4 w-4" /> All programs
      </button>

      <input
        value={prog.name} onChange={(e) => setField('name', e.target.value)}
        className="w-full font-display font-bold text-2xl bg-transparent border-b border-transparent hover:border-input focus:border-primary focus-visible:outline-none mb-1 pb-1"
      />
      <input
        value={prog.description || ''} onChange={(e) => setField('description', e.target.value)}
        placeholder="Description (optional)"
        className="w-full text-sm text-muted-foreground bg-transparent focus-visible:outline-none mb-5"
      />

      <div className="space-y-8">
        {prog.days.map((day, di) => (
          <DayEditor
            key={di} day={day} di={di}
            onRemove={() => removeDay(di)} onUpdate={(k, v) => updateDay(di, k, v)}
            onAddBlock={() => addBlock(di)} onRemoveBlock={(bi) => removeBlock(di, bi)}
            onUpdateBlock={(bi, k, v) => updateBlock(di, bi, k, v)} onMoveBlock={(bi, dir) => moveBlock(di, bi, dir)}
            onAddExercise={(bi) => setPicker({ dayIdx: di, blockIdx: bi })}
            onRemoveExercise={(bi, ei) => removeExercise(di, bi, ei)}
            onUpdateExercise={(bi, ei, k, v) => updateExercise(di, bi, ei, k, v)}
            onMoveExercise={(bi, ei, dir) => moveExercise(di, bi, ei, dir)}
          />
        ))}
      </div>

      <Button variant="outline" className="w-full mt-4" onClick={addDay}><Plus className="h-4 w-4 mr-1" /> Add a day</Button>

      <Button className="w-full h-12 mt-6" onClick={save} disabled={saving}><Save className="h-4 w-4 mr-1" /> {saving ? 'Saving…' : 'Save program'}</Button>
      {saved && <p className="text-center text-sm text-primary mt-2">Saved ✓</p>}

      {picker && (
        <ExercisePicker
          api={api} token={token}
          onPick={(lib) => { addExercise(picker.dayIdx, picker.blockIdx, lib); setPicker(null); }}
          onClose={() => setPicker(null)}
        />
      )}
    </div>
  );
};

// ── One day card ────────────────────────────────────────────────────────────
const DayEditor = ({ day, di, onRemove, onUpdate, onAddBlock, onRemoveBlock, onUpdateBlock, onMoveBlock, onAddExercise, onRemoveExercise, onUpdateExercise, onMoveExercise }) => (
  <div className="rounded-xl border-2 border-border bg-card p-3 shadow-sm">
    <div className="flex items-center justify-between mb-3 pb-2 border-b">
      <span className="font-display font-bold text-sm uppercase tracking-wide text-primary">Day {di + 1}{day.focus ? ` · ${day.focus}` : ''}</span>
      <button onClick={onRemove} className="h-8 w-8 rounded-md border flex items-center justify-center text-muted-foreground hover:text-destructive hover:border-destructive" aria-label="Remove day">
        <Trash2 className="h-4 w-4" />
      </button>
    </div>
    <div className="flex items-center gap-2 mb-3">
      <select value={day.week_no} onChange={(e) => onUpdate('week_no', Number(e.target.value))}
        className="h-9 rounded-md border border-input bg-background px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
        {Array.from({ length: 16 }, (_, i) => i + 1).map((w) => <option key={w} value={w}>Week {w}</option>)}
      </select>
      <select value={day.day_of_week} onChange={(e) => onUpdate('day_of_week', Number(e.target.value))}
        className="h-9 rounded-md border border-input bg-background px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
        {DOW.map((d, i) => <option key={i} value={i}>{d}</option>)}
      </select>
      <input value={day.focus || ''} onChange={(e) => onUpdate('focus', e.target.value)} placeholder="Focus (e.g. Legs)"
        className="flex-1 h-9 rounded-md border border-input bg-background px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" />
    </div>

    <div className="space-y-3">
      {day.blocks.map((block, bi) => (
        <BlockEditor
          key={bi} block={block} bi={bi} lastBlock={bi === day.blocks.length - 1}
          onRemove={() => onRemoveBlock(bi)} onUpdate={(k, v) => onUpdateBlock(bi, k, v)} onMove={(dir) => onMoveBlock(bi, dir)}
          onAddExercise={() => onAddExercise(bi)} onRemoveExercise={(ei) => onRemoveExercise(bi, ei)}
          onUpdateExercise={(ei, k, v) => onUpdateExercise(bi, ei, k, v)} onMoveExercise={(ei, dir) => onMoveExercise(bi, ei, dir)}
        />
      ))}
    </div>

    <button onClick={onAddBlock} className="flex items-center gap-1 text-sm text-primary hover:underline mt-3">
      <Plus className="h-4 w-4" /> Add block
    </button>
  </div>
);

// ── One block (single / superset / circuit) ─────────────────────────────────
const BlockEditor = ({ block, bi, lastBlock, onRemove, onUpdate, onMove, onAddExercise, onRemoveExercise, onUpdateExercise, onMoveExercise }) => (
  <div className="rounded-lg border bg-background p-2.5">
    <div className="flex items-center gap-2 mb-2">
      <select value={block.type} onChange={(e) => onUpdate('type', e.target.value)}
        className="h-8 rounded-md border border-input bg-background px-2 text-xs font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
        {BLOCK_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
      </select>
      {block.type !== 'single' && (
        <input type="number" min="1" value={block.rounds ?? ''} onChange={(e) => onUpdate('rounds', e.target.value === '' ? null : Number(e.target.value))}
          placeholder="Rounds" className="h-8 w-20 rounded-md border border-input bg-background px-2 text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" />
      )}
      <div className="flex-1" />
      <button onClick={() => onMove(-1)} disabled={bi === 0} className="h-8 w-7 rounded-md border flex items-center justify-center text-muted-foreground disabled:opacity-30" aria-label="Move block up"><ChevronUp className="h-4 w-4" /></button>
      <button onClick={() => onMove(1)} disabled={lastBlock} className="h-8 w-7 rounded-md border flex items-center justify-center text-muted-foreground disabled:opacity-30" aria-label="Move block down"><ChevronDown className="h-4 w-4" /></button>
      <button onClick={onRemove} className="h-8 w-7 rounded-md border flex items-center justify-center text-muted-foreground hover:text-destructive" aria-label="Remove block"><Trash2 className="h-4 w-4" /></button>
    </div>

    <div className="space-y-2">
      {block.exercises.map((ex, ei) => (
        <div key={ei} className="rounded-md border bg-card p-2">
          <div className="flex items-center gap-2 mb-2">
            {ex.youtube_video_id
              ? <img src={`https://i.ytimg.com/vi/${ex.youtube_video_id}/default.jpg`} alt="" className="w-12 h-8 object-cover rounded flex-none" />
              : <div className="w-12 h-8 rounded bg-muted flex items-center justify-center flex-none"><Dumbbell className="h-3.5 w-3.5 text-muted-foreground/50" /></div>}
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{ex.name}</div>
              <div className="text-[11px] text-primary">{ex.muscle_group}</div>
            </div>
            <button onClick={() => onMoveExercise(ei, -1)} disabled={ei === 0} className="text-muted-foreground disabled:opacity-30" aria-label="Move up"><ChevronUp className="h-4 w-4" /></button>
            <button onClick={() => onMoveExercise(ei, 1)} disabled={ei === block.exercises.length - 1} className="text-muted-foreground disabled:opacity-30" aria-label="Move down"><ChevronDown className="h-4 w-4" /></button>
            <button onClick={() => onRemoveExercise(ei)} className="text-muted-foreground hover:text-destructive" aria-label="Remove exercise"><X className="h-4 w-4" /></button>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <LabeledInput label="Sets" type="number" value={ex.target_sets ?? ''} onChange={(v) => onUpdateExercise(ei, 'target_sets', v === '' ? null : Number(v))} />
            <LabeledInput label="Reps" value={ex.reps_text || ''} onChange={(v) => onUpdateExercise(ei, 'reps_text', v)} placeholder="8-10" />
            <LabeledInput label="Weight (lb)" type="number" value={ex.suggested_weight ?? ''} onChange={(v) => onUpdateExercise(ei, 'suggested_weight', v)} placeholder="0 = BW" />
          </div>
          <input value={ex.coach_note || ''} onChange={(e) => onUpdateExercise(ei, 'coach_note', e.target.value)} placeholder="Coach note (optional)"
            className="w-full h-8 mt-2 rounded-md border border-input bg-background px-2 text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" />
        </div>
      ))}
    </div>

    <button onClick={onAddExercise} className="flex items-center gap-1 text-xs text-primary hover:underline mt-2">
      <Plus className="h-3.5 w-3.5" /> Add exercise
    </button>
  </div>
);

const LabeledInput = ({ label, value, onChange, type = 'text', placeholder }) => (
  <label className="block">
    <span className="text-[10px] text-muted-foreground">{label}</span>
    <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
      className="w-full h-8 rounded-md border border-input bg-background px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" />
  </label>
);

// ── Exercise picker (from the library) ──────────────────────────────────────
const ExercisePicker = ({ api, token, onPick, onClose }) => {
  useBackHandler(true, onClose);
  const [q, setQ] = useState('');
  const [group, setGroup] = useState('All');
  const [groups, setGroups] = useState([]);
  const [list, setList] = useState([]);

  useEffect(() => { api.get('/exercises/groups', token).then(setGroups).catch(() => setGroups([])); }, [api, token]);
  useEffect(() => {
    const t = setTimeout(() => {
      const params = [];
      if (group !== 'All') params.push(`group=${encodeURIComponent(group)}`);
      if (q.trim()) params.push(`q=${encodeURIComponent(q.trim())}`);
      api.get(`/exercises${params.length ? `?${params.join('&')}` : ''}`, token).then(setList).catch(() => setList([]));
    }, 200);
    return () => clearTimeout(t);
  }, [api, token, q, group]);

  return (
    <div className="fixed inset-0 z-50 flex items-stretch sm:items-center justify-center bg-black/60" onClick={onClose}>
      <div className="w-full sm:max-w-md bg-card sm:border sm:rounded-xl h-full sm:h-auto sm:max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()} style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}>
        <div className="p-3 border-b flex-none space-y-2">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search exercises…"
                className="w-full h-10 rounded-md border border-input bg-background pl-8 pr-3 text-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" />
            </div>
            <button onClick={onClose} aria-label="Close"><X className="h-5 w-5 text-muted-foreground" /></button>
          </div>
          <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1">
            {['All', ...groups.map((g) => g.muscle_group)].map((g) => (
              <button key={g} onClick={() => setGroup(g)}
                className={`flex-none px-3 h-7 rounded-full text-xs font-medium border ${group === g ? 'bg-primary text-primary-foreground border-primary' : 'bg-background text-muted-foreground border-input'}`}>
                {g}
              </button>
            ))}
          </div>
        </div>
        <div className="overflow-y-auto p-2 space-y-1">
          {list.map((ex) => (
            <button key={ex.id} onClick={() => onPick(ex)} className="w-full text-left flex items-center gap-3 rounded-md p-2 hover:bg-accent">
              {ex.youtube_video_id
                ? <img src={`https://i.ytimg.com/vi/${ex.youtube_video_id}/default.jpg`} alt="" className="w-14 h-9 object-cover rounded flex-none" />
                : <div className="w-14 h-9 rounded bg-muted flex items-center justify-center flex-none"><Dumbbell className="h-4 w-4 text-muted-foreground/50" /></div>}
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">{ex.name}</div>
                <div className="text-xs text-primary">{ex.muscle_group}</div>
              </div>
            </button>
          ))}
          {list.length === 0 && <p className="text-sm text-muted-foreground text-center py-6">No exercises found.</p>}
        </div>
      </div>
    </div>
  );
};

// ── Assign a program to a user ──────────────────────────────────────────────
const AssignModal = ({ api, token, program, onClose, onChanged }) => {
  useBackHandler(true, onClose);
  const [users, setUsers] = useState([]);
  const [assigned, setAssigned] = useState([]);
  const [userId, setUserId] = useState('');
  const [startSunday, setStartSunday] = useState(nextSunday());
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadAssigned = useCallback(
    () => api.get(`/programs/${program.id}/assignments`, token).then(setAssigned).catch(() => setAssigned([])),
    [api, token, program.id]);
  useEffect(() => { api.get('/users', token).then(setUsers).catch(() => setUsers([])); loadAssigned(); }, [api, token, loadAssigned]);

  const isSunday = (() => { const d = new Date(startSunday + 'T00:00:00'); return d.getDay() === 0; })();

  const assign = async () => {
    if (!userId || !isSunday) return;
    setBusy(true); setMsg(null);
    try {
      await api.post(`/programs/${program.id}/assign`, { user_id: Number(userId), start_sunday: startSunday }, token);
      setMsg({ ok: true, text: 'Assigned ✓' });
      setUserId('');
      loadAssigned();
      onChanged && onChanged();
    } catch (e) { setMsg({ ok: false, text: e.message || 'Failed to assign' }); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60" onClick={onClose}>
      <div className="w-full sm:max-w-md bg-card border-t sm:border rounded-t-xl sm:rounded-xl p-4"
        onClick={(e) => e.stopPropagation()} style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 1rem)' }}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold">Assign “{program.name}”</h3>
          <button onClick={onClose} aria-label="Close"><X className="h-5 w-5 text-muted-foreground" /></button>
        </div>

        <div className="mb-4">
          <div className="text-xs text-muted-foreground mb-1">Currently on this program</div>
          {assigned.length === 0 ? (
            <div className="text-sm text-muted-foreground">No one yet.</div>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {assigned.map((a) => <span key={a.id} className="text-xs bg-muted rounded-full px-2 py-1">{a.display_name}</span>)}
            </div>
          )}
        </div>

        <label className="block text-sm mb-1 text-muted-foreground">Client</label>
        <select value={userId} onChange={(e) => setUserId(e.target.value)}
          className="w-full h-10 rounded-md border border-input bg-background px-2 text-base mb-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
          <option value="">Select a user…</option>
          {users.map((u) => <option key={u.id} value={u.id}>{u.display_name} ({u.role})</option>)}
        </select>

        <label className="block text-sm mb-1 text-muted-foreground">Start date</label>
        <input type="date" value={startSunday} onChange={(e) => e.target.value && setStartSunday(sundayOnAfter(e.target.value))}
          className="w-full h-10 rounded-md border border-input bg-background px-2 text-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" />
        <p className="text-xs text-muted-foreground mt-1">Weeks start Sunday — program starts {fmtSunday(startSunday)}</p>

        {msg && <p className={`text-sm mt-3 ${msg.ok ? 'text-primary' : 'text-destructive'}`}>{msg.text}</p>}

        <Button className="w-full h-11 mt-4" onClick={assign} disabled={busy || !userId || !isSunday}>
          {busy ? 'Assigning…' : 'Assign program'}
        </Button>
      </div>
    </div>
  );
};

// ── Clients roster: see each user's program, reassign ───────────────────────
const ClientsView = ({ api, token, viewer }) => {
  const [users, setUsers] = useState(null);
  const [reassign, setReassign] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null); // user pending delete
  const [delErr, setDelErr] = useState(null);
  const [delBusy, setDelBusy] = useState(false);
  const [detailClient, setDetailClient] = useState(null); // client whose progress is open

  const load = useCallback(() => api.get('/users', token).then(setUsers).catch(() => setUsers([])), [api, token]);
  useEffect(() => { load(); }, [load]);

  const changeRole = async (u, role) => {
    if (role === u.role) return;
    try { await api.put(`/users/${u.id}/role`, { role }, token); load(); } catch { /* ignore */ }
  };

  const changeTone = async (u, coach_tone) => {
    if (coach_tone === (u.coach_tone || 'encouraging')) return;
    try { await api.put(`/users/${u.id}/coach-tone`, { coach_tone }, token); load(); } catch { /* ignore */ }
  };

  const removeAssignment = async (id) => {
    if (!id) return;
    try { await api.delete(`/assignments/${id}`, token); load(); } catch { /* ignore */ }
  };

  const removeUser = async () => {
    setDelBusy(true); setDelErr(null);
    try {
      await api.delete(`/users/${confirmDel.id}`, token);
      setConfirmDel(null);
      load();
    } catch (e) {
      setDelErr(e?.message || 'Could not delete this user.');
    } finally { setDelBusy(false); }
  };

  useBackHandler(!!confirmDel, () => setConfirmDel(null));

  if (detailClient) return <ClientDetail api={api} token={token} client={detailClient} onBack={() => setDetailClient(null)} />;
  if (users === null) return <p className="text-center text-muted-foreground py-12">Loading…</p>;
  if (users.length === 0) return <p className="text-center text-muted-foreground py-12">No users.</p>;

  return (
    <div className="space-y-2">
      {users.map((u) => (
        <div key={u.id} className="rounded-lg border bg-card p-3">
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <button onClick={() => setDetailClient(u)} className="font-medium truncate text-left hover:text-primary block max-w-full">{u.display_name}</button>
              <div className="text-xs text-muted-foreground truncate">{u.email}</div>
              <div className="text-xs truncate flex items-center gap-1">
                {u.program_name
                  ? <><span className="text-primary truncate">On: {u.program_name}{u.assigned_by_name ? <span className="text-muted-foreground"> · by {u.assigned_by_name}</span> : ''}</span>
                      <button onClick={() => removeAssignment(u.current_assignment_id)} aria-label="Remove current program" className="text-muted-foreground hover:text-destructive flex-none"><X className="h-3 w-3" /></button></>
                  : <span className="text-muted-foreground">No program assigned</span>}
              </div>
              {u.next_program_name && (
                <div className="text-xs text-muted-foreground truncate flex items-center gap-1">
                  <span className="truncate">Next: {u.next_program_name} · starts {u.next_start}</span>
                  <button onClick={() => removeAssignment(u.next_assignment_id)} aria-label="Remove queued program" className="hover:text-destructive flex-none"><X className="h-3 w-3" /></button>
                </div>
              )}
            </div>
            <Button variant="outline" className="h-9 flex-none" onClick={() => setReassign(u)}>{u.program_name ? 'Assign next' : 'Assign'}</Button>
          </div>
          {viewer.role === 'admin' ? (
            <div className="flex items-center gap-2 mt-2">
              <span className="text-xs text-muted-foreground">Role</span>
              <select value={u.role} onChange={(e) => changeRole(u, e.target.value)}
                className="h-8 rounded-md border border-input bg-background px-2 text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                {ROLES.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
              </select>
              {u.id !== viewer.id && (
                <button onClick={() => { setDelErr(null); setConfirmDel(u); }}
                  className="ml-auto h-8 w-8 rounded-md border flex items-center justify-center text-muted-foreground hover:text-destructive hover:border-destructive/50"
                  aria-label="Delete user" title="Delete user">
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
          ) : (
            <div className="text-xs text-muted-foreground mt-1">{ROLE_LABELS[u.role] || u.role}</div>
          )}
          <div className="flex items-center gap-2 mt-2">
            <span className="text-xs text-muted-foreground">Coach tone</span>
            <select value={u.coach_tone || 'encouraging'} onChange={(e) => changeTone(u, e.target.value)}
              className="h-8 rounded-md border border-input bg-background px-2 text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
              {COACH_TONES.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
            </select>
          </div>
        </div>
      ))}
      {reassign && <ReassignModal api={api} token={token} user={reassign} onClose={() => setReassign(null)} onDone={() => { setReassign(null); load(); }} />}

      {confirmDel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => !delBusy && setConfirmDel(null)}>
          <div className="w-full max-w-xs bg-card border rounded-xl p-5 text-center" onClick={(e) => e.stopPropagation()}>
            <Trash2 className="h-8 w-8 mx-auto text-destructive mb-2" />
            <h3 className="font-display font-bold text-lg mb-1">Delete {confirmDel.display_name}?</h3>
            <p className="text-sm text-muted-foreground mb-4">This permanently removes the account and all their workout history, PRs, and notes. This can't be undone.</p>
            {delErr && <p className="text-sm text-destructive mb-3">{delErr}</p>}
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" disabled={delBusy} onClick={() => setConfirmDel(null)}>Cancel</Button>
              <Button variant="destructive" className="flex-1" disabled={delBusy} onClick={removeUser}>{delBusy ? 'Deleting…' : 'Delete'}</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Trainer/admin: a client's training progress + workout history ────────────
const ClientDetail = ({ api, token, client, onBack }) => {
  useBackHandler(true, onBack);
  const [data, setData] = useState(null);
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    api.get(`/users/${client.id}/progress`, token)
      .then(setData)
      .catch(() => setData({ user: client, streak: 0, total: 0, sessions: [] }));
  }, [api, token, client.id]);

  if (!data) return <p className="text-center text-muted-foreground py-12">Loading…</p>;

  return (
    <div className="pb-4">
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-3">
        <ArrowLeft className="h-4 w-4" /> All clients
      </button>
      <h2 className="font-display font-bold text-2xl leading-tight">{data.user.display_name}</h2>
      <p className="text-sm text-muted-foreground mb-4">{data.user.email}</p>

      <div className="grid grid-cols-2 gap-2 mb-5">
        <div className="rounded-lg border bg-card p-3"><div className="text-2xl font-bold">{data.total}</div><div className="text-xs text-muted-foreground">Workouts completed</div></div>
        <div className="rounded-lg border bg-card p-3"><div className="text-2xl font-bold">{data.streak}</div><div className="text-xs text-muted-foreground">Current streak</div></div>
      </div>

      <h3 className="font-semibold mb-2">Recent workouts</h3>
      {data.sessions.length === 0 ? (
        <p className="text-sm text-muted-foreground">No completed workouts yet.</p>
      ) : (
        <div className="space-y-2">
          {data.sessions.map((s) => (
            <button key={s.id} onClick={() => setOpenId(s.id)} className="w-full text-left rounded-lg border bg-card p-3 hover:border-primary">
              <div className="flex justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-medium truncate">{s.label || 'Workout'}</div>
                  <div className="text-xs text-muted-foreground">{fmtDateLong(s.date)}</div>
                </div>
                <div className="text-xs text-muted-foreground text-right flex-none">
                  {s.sets} sets · {Math.round(Number(s.volume)).toLocaleString()} lb<br />{fmtDur(s.duration)}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {openId && <SessionDetailModal api={api} token={token} sessionId={openId} onClose={() => setOpenId(null)} />}
    </div>
  );
};

const SessionDetailModal = ({ api, token, sessionId, onClose }) => {
  useBackHandler(true, onClose);
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/sessions/${sessionId}`, token).then(setD).catch(() => setD(null)); }, [api, token, sessionId]);

  const grouped = {};
  (d?.sets || []).forEach((s) => { (grouped[s.exercise_name || `#${s.exercise_id}`] ||= []).push(s); });

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60" onClick={onClose}>
      <div className="w-full sm:max-w-md bg-card border-t sm:border rounded-t-xl sm:rounded-xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-4 py-3 border-b flex items-center justify-between flex-none">
          <div className="font-semibold truncate">{d ? (d.label || 'Workout') : 'Loading…'}</div>
          <button onClick={onClose} aria-label="Close"><X className="h-5 w-5 text-muted-foreground" /></button>
        </div>
        <div className="p-4 overflow-y-auto" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 1rem)' }}>
          {!d ? (
            <p className="text-sm text-muted-foreground text-center py-6">Loading…</p>
          ) : (
            <>
              {d.ai_assessment && (
                <div className="rounded-lg bg-muted/50 border p-3 mb-4">
                  <div className="text-xs font-semibold text-primary mb-1">Coach's feedback</div>
                  <p className="text-sm leading-relaxed">{d.ai_assessment}</p>
                </div>
              )}
              <div className="space-y-2">
                {Object.entries(grouped).map(([name, ss]) => (
                  <div key={name} className="rounded-lg border bg-background p-2.5">
                    <div className="text-sm font-medium mb-1.5">{name}</div>
                    <div className="space-y-1">
                      {ss.map((s, j) => (
                        <div key={j} className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Set {s.set_number || j + 1}</span>
                          <span className="font-medium">{s.weight != null ? Number(s.weight) : 'BW'}×{s.reps}{s.rpe ? ` @${Number(s.rpe)}` : ''}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
                {Object.keys(grouped).length === 0 && <p className="text-sm text-muted-foreground">No sets logged.</p>}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Power user: pick a saved program for your own calendar ───────────────────
const MyProgramPicker = ({ api, token }) => {
  const [programs, setPrograms] = useState(null);
  const [current, setCurrent] = useState(null);
  const [pick, setPick] = useState(null);

  const load = useCallback(() => {
    api.get('/me/assignment', token).then((d) => setCurrent(d.assignment || null)).catch(() => setCurrent(null));
    api.get('/programs', token).then(setPrograms).catch(() => setPrograms([]));
  }, [api, token]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="max-w-2xl mx-auto p-4">
      <h2 className="font-display font-bold text-2xl mb-1">My Program</h2>
      <p className="text-sm mb-4">
        {current
          ? <span className="text-primary">Currently on: {current.program_name} (since {String(current.start_sunday).slice(0, 10)})</span>
          : <span className="text-muted-foreground">No program yet — pick one below to put it on your calendar.</span>}
      </p>
      {programs === null ? (
        <p className="text-center text-muted-foreground py-12">Loading…</p>
      ) : programs.length === 0 ? (
        <p className="text-center text-muted-foreground py-12">No programs available yet.</p>
      ) : (
        <div className="space-y-2">
          {programs.map((p) => (
            <div key={p.id} className="rounded-lg border bg-card p-3 flex items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="font-medium truncate">{p.name}</div>
                <div className="text-xs text-muted-foreground">{p.weeks} {p.weeks === 1 ? 'week' : 'weeks'} · {p.day_count} {p.day_count === 1 ? 'day' : 'days'}</div>
              </div>
              <Button className="h-9 flex-none" onClick={() => setPick(p)}>Use this</Button>
            </div>
          ))}
        </div>
      )}
      {pick && <SelfAssignModal api={api} token={token} program={pick} onClose={() => setPick(null)} onDone={() => { setPick(null); load(); }} />}
    </div>
  );
};

// Power user self-assigns a program (server forces user = self).
const SelfAssignModal = ({ api, token, program, onClose, onDone }) => {
  useBackHandler(true, onClose);
  const [startSunday, setStartSunday] = useState(nextSunday());
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const isSunday = (() => { const d = new Date(startSunday + 'T00:00:00'); return d.getDay() === 0; })();

  const assign = async () => {
    if (!isSunday) return;
    setBusy(true); setMsg(null);
    try {
      await api.post(`/programs/${program.id}/assign`, { start_sunday: startSunday }, token);
      onDone();
    } catch (e) { setMsg(e.message || 'Failed to assign'); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60" onClick={onClose}>
      <div className="w-full sm:max-w-md bg-card border-t sm:border rounded-t-xl sm:rounded-xl p-4"
        onClick={(e) => e.stopPropagation()} style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 1rem)' }}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold">Start “{program.name}”</h3>
          <button onClick={onClose} aria-label="Close"><X className="h-5 w-5 text-muted-foreground" /></button>
        </div>
        <label className="block text-sm mb-1 text-muted-foreground">Start date</label>
        <input type="date" value={startSunday} onChange={(e) => e.target.value && setStartSunday(sundayOnAfter(e.target.value))}
          className="w-full h-10 rounded-md border border-input bg-background px-2 text-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" />
        <p className="text-xs text-muted-foreground mt-1">Weeks start Sunday — program starts {fmtSunday(startSunday)}</p>
        {msg && <p className="text-sm text-destructive mt-3">{msg}</p>}
        <Button className="w-full h-11 mt-4" onClick={assign} disabled={busy || !isSunday}>
          {busy ? 'Assigning…' : 'Put on my calendar'}
        </Button>
      </div>
    </div>
  );
};

// Assign/switch a single user's program (the per-user counterpart of AssignModal).
const ReassignModal = ({ api, token, user, onClose, onDone }) => {
  useBackHandler(true, onClose);
  const [programs, setPrograms] = useState([]);
  const [programId, setProgramId] = useState('');
  const [startSunday, setStartSunday] = useState(nextSunday());
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get('/programs', token).then(setPrograms).catch(() => setPrograms([])); }, [api, token]);
  const isSunday = (() => { const d = new Date(startSunday + 'T00:00:00'); return d.getDay() === 0; })();

  const assign = async () => {
    if (!programId || !isSunday) return;
    setBusy(true); setMsg(null);
    try {
      await api.post(`/programs/${programId}/assign`, { user_id: user.id, start_sunday: startSunday }, token);
      onDone();
    } catch (e) { setMsg(e.message || 'Failed to assign'); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60" onClick={onClose}>
      <div className="w-full sm:max-w-md bg-card border-t sm:border rounded-t-xl sm:rounded-xl p-4"
        onClick={(e) => e.stopPropagation()} style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 1rem)' }}>
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-semibold">Assign a program</h3>
          <button onClick={onClose} aria-label="Close"><X className="h-5 w-5 text-muted-foreground" /></button>
        </div>
        <p className="text-sm text-muted-foreground mb-4">{user.display_name} · currently {user.program_name ? `on "${user.program_name}"` : 'no program'}</p>

        <label className="block text-sm mb-1 text-muted-foreground">Program</label>
        <select value={programId} onChange={(e) => setProgramId(e.target.value)}
          className="w-full h-10 rounded-md border border-input bg-background px-2 text-base mb-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
          <option value="">Select a program…</option>
          {programs.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>

        <label className="block text-sm mb-1 text-muted-foreground">Start date</label>
        <input type="date" value={startSunday} onChange={(e) => e.target.value && setStartSunday(sundayOnAfter(e.target.value))}
          className="w-full h-10 rounded-md border border-input bg-background px-2 text-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" />
        <p className="text-xs text-muted-foreground mt-1">Weeks start Sunday — program starts {fmtSunday(startSunday)}</p>
        {msg && <p className="text-sm text-destructive mt-3">{msg}</p>}

        <Button className="w-full h-11 mt-4" onClick={assign} disabled={busy || !programId || !isSunday}>
          {busy ? 'Assigning…' : 'Assign program'}
        </Button>
      </div>
    </div>
  );
};

// ── Warmups (trainer/admin): list ↔ editor ──────────────────────────────────
const WarmupManager = ({ api, token }) => {
  const [editId, setEditId] = useState(null);
  if (editId != null) return <WarmupEditor api={api} token={token} warmupId={editId} onBack={() => setEditId(null)} />;
  return <WarmupList api={api} token={token} onEdit={setEditId} />;
};

const WarmupList = ({ api, token, onEdit }) => {
  const [warmups, setWarmups] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');

  const load = useCallback(() => api.get('/warmups', token).then(setWarmups).catch(() => setWarmups([])), [api, token]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    const name = newName.trim();
    if (!name) return;
    try { const { id } = await api.post('/warmups', { name }, token); setCreating(false); setNewName(''); onEdit(id); } catch { /* ignore */ }
  };
  const remove = async (w) => {
    if (!window.confirm(`Delete warmup "${w.name}"?`)) return;
    try { await api.delete(`/warmups/${w.id}`, token); load(); } catch { /* ignore */ }
  };

  return (
    <div>
      <div className="flex justify-end mb-3">
        {!creating && <Button onClick={() => setCreating(true)}><Plus className="h-4 w-4 mr-1" /> New warmup</Button>}
      </div>
      {creating && (
        <div className="flex gap-2 mb-4">
          <input autoFocus value={newName} onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && create()}
            placeholder="Warmup name" className="flex-1 h-10 rounded-md border border-input bg-background px-3 text-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" />
          <Button onClick={create}>Create</Button>
          <button onClick={() => { setCreating(false); setNewName(''); }} className="text-sm text-muted-foreground px-2">Cancel</button>
        </div>
      )}
      {warmups === null ? (
        <p className="text-center text-muted-foreground py-12">Loading…</p>
      ) : warmups.length === 0 ? (
        <div className="text-center text-muted-foreground py-12"><Layers className="h-10 w-10 mx-auto mb-3 opacity-40" /><p>No warmups yet. Create one for users to pick before a workout.</p></div>
      ) : (
        <div className="space-y-2">
          {warmups.map((w) => (
            <div key={w.id} className="rounded-lg border bg-card p-3 flex items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="font-medium truncate">{w.name}</div>
                <div className="text-xs text-muted-foreground">{w.exercise_count} {w.exercise_count === 1 ? 'move' : 'moves'}</div>
              </div>
              <div className="flex gap-2 flex-none">
                <Button variant="outline" className="h-9" onClick={() => onEdit(w.id)}><Pencil className="h-4 w-4 mr-1" /> Edit</Button>
                <button onClick={() => remove(w)} className="h-9 w-9 rounded-md border flex items-center justify-center text-muted-foreground hover:text-destructive hover:border-destructive" aria-label="Delete"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const WarmupEditor = ({ api, token, warmupId, onBack }) => {
  useBackHandler(true, onBack);
  const [w, setW] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [picker, setPicker] = useState(false);

  useEffect(() => {
    api.get(`/warmups/${warmupId}`, token)
      .then((d) => setW({ ...d, exercises: d.exercises || [] }))
      .catch(() => setW(null));
  }, [api, token, warmupId]);

  const setField = (k, v) => setW((p) => ({ ...p, [k]: v }));
  const addEx = (lib) => setW((p) => ({ ...p, exercises: [...p.exercises, { exercise_id: lib.id, name: lib.name, youtube_video_id: lib.youtube_video_id, muscle_group: lib.muscle_group, target_text: '', coach_note: '' }] }));
  const updateEx = (i, k, v) => setW((p) => ({ ...p, exercises: p.exercises.map((e, j) => (j === i ? { ...e, [k]: v } : e)) }));
  const removeEx = (i) => setW((p) => ({ ...p, exercises: p.exercises.filter((_, j) => j !== i) }));
  const moveEx = (i, dir) => setW((p) => { const a = [...p.exercises]; const j = i + dir; if (j < 0 || j >= a.length) return p; [a[i], a[j]] = [a[j], a[i]]; return { ...p, exercises: a }; });

  const save = async () => {
    setSaving(true); setSaved(false);
    try {
      await api.put(`/warmups/${w.id}`, { name: w.name, description: w.description, exercises: w.exercises }, token);
      setSaved(true); setTimeout(() => setSaved(false), 2000);
    } catch { /* ignore */ } finally { setSaving(false); }
  };

  if (!w) return <p className="text-center text-muted-foreground py-16">Loading…</p>;

  return (
    <div className="pb-4">
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-3">
        <ArrowLeft className="h-4 w-4" /> All warmups
      </button>
      <input value={w.name} onChange={(e) => setField('name', e.target.value)}
        className="w-full font-display font-bold text-2xl bg-transparent border-b border-transparent hover:border-input focus:border-primary focus-visible:outline-none mb-1 pb-1" />
      <input value={w.description || ''} onChange={(e) => setField('description', e.target.value)} placeholder="Description (optional)"
        className="w-full text-sm text-muted-foreground bg-transparent focus-visible:outline-none mb-5" />

      <div className="space-y-2">
        {w.exercises.map((ex, i) => (
          <div key={i} className="rounded-md border bg-card p-2">
            <div className="flex items-center gap-2 mb-2">
              {ex.youtube_video_id
                ? <img src={`https://i.ytimg.com/vi/${ex.youtube_video_id}/default.jpg`} alt="" className="w-12 h-8 object-cover rounded flex-none" />
                : <div className="w-12 h-8 rounded bg-muted flex items-center justify-center flex-none"><Dumbbell className="h-3.5 w-3.5 text-muted-foreground/50" /></div>}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{ex.name}</div>
                <div className="text-[11px] text-primary">{ex.muscle_group}</div>
              </div>
              <button onClick={() => moveEx(i, -1)} disabled={i === 0} className="text-muted-foreground disabled:opacity-30" aria-label="Move up"><ChevronUp className="h-4 w-4" /></button>
              <button onClick={() => moveEx(i, 1)} disabled={i === w.exercises.length - 1} className="text-muted-foreground disabled:opacity-30" aria-label="Move down"><ChevronDown className="h-4 w-4" /></button>
              <button onClick={() => removeEx(i)} className="text-muted-foreground hover:text-destructive" aria-label="Remove"><X className="h-4 w-4" /></button>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <input value={ex.target_text || ''} onChange={(e) => updateEx(i, 'target_text', e.target.value)} placeholder="Target (e.g. 10 reps, 30 sec)"
                className="h-9 rounded-md border border-input bg-background px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" />
              <input value={ex.coach_note || ''} onChange={(e) => updateEx(i, 'coach_note', e.target.value)} placeholder="Note (optional)"
                className="h-9 rounded-md border border-input bg-background px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" />
            </div>
          </div>
        ))}
      </div>

      <button onClick={() => setPicker(true)} className="flex items-center gap-1 text-sm text-primary hover:underline mt-3">
        <Plus className="h-4 w-4" /> Add move
      </button>

      <Button className="w-full h-12 mt-6" onClick={save} disabled={saving}><Save className="h-4 w-4 mr-1" /> {saving ? 'Saving…' : 'Save warmup'}</Button>
      {saved && <p className="text-center text-sm text-primary mt-2">Saved ✓</p>}

      {picker && <ExercisePicker api={api} token={token} onPick={(lib) => { addEx(lib); setPicker(false); }} onClose={() => setPicker(false)} />}
    </div>
  );
};

// Next Sunday on/after today, as YYYY-MM-DD (local).
function nextSunday() {
  const d = new Date();
  d.setDate(d.getDate() + ((7 - d.getDay()) % 7));
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// Snap any picked date to the Sunday on/after it, so a calendar pick is always a
// valid (Sunday) program start — no "pick a Sunday" error.
function sundayOnAfter(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  d.setDate(d.getDate() + ((7 - d.getDay()) % 7));
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function fmtSunday(dateStr) {
  return new Date(dateStr + 'T00:00:00').toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' });
}

function fmtDateLong(dateStr) {
  return new Date(dateStr + 'T00:00:00').toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
}

function fmtDur(sec) {
  if (sec == null) return '—';
  sec = Math.max(0, Math.floor(sec));
  const h = Math.floor(sec / 3600), m = Math.floor((sec % 3600) / 60), s = sec % 60;
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}` : `${m}:${String(s).padStart(2, '0')}`;
}

export default ProgramView;
